/**
 * Data Hub resource hooks.
 *
 * These hooks bind resources that still require dedicated generated clients.
 * Descriptor-native resources intentionally omit a hook so the generic runtime
 * executes the backend-resolved query binding instead.
 */
import {
  useOrganizationClientsList as useOrganizationClientsListQuery,
  useOrganizationClientsRead as useOrganizationClientsReadQuery,
  useOrganizationPortfoliosList as useOrganizationPortfoliosListQuery,
  useOrganizationPortfoliosRead as useOrganizationPortfoliosReadQuery,
  useInvestmentFactPosition as useInvestmentFactPositionQuery,
  useInvestmentFactCgt as useInvestmentFactCgtQuery,
  useAccountingJournalList as useAccountingJournalListQuery,
  useAccountingJournalGet as useAccountingJournalGetQuery,
  useAccountingFiscalPeriodList as useAccountingFiscalPeriodListQuery,
  type ClientAccountSurfaceDto,
  type PortfolioAccountSurfaceDto,
  type PositionFactSurfaceDto,
} from '../../api/generated/client';
import { ROUTE_IDS, OPERATION_IDS } from '@finance/contracts';
import type {
  ListHookResult,
  GetHookResult,
  ResourceHookBinding,
} from '../resource-hook-registry.js';
import { selectApiData, type ApiSuccessEnvelope } from '../../api/select-api-data.js';

function selectListRows<T>(response: unknown): T[] {
  return selectApiData(response as ApiSuccessEnvelope<T[]>) ?? [];
}

function selectDetail<T>(response: unknown): T {
  return selectApiData(response as ApiSuccessEnvelope<T>);
}

// ── Clients ────────────────────────────────────────────────────────────────

export function useDataClientList(): ListHookResult<ClientAccountSurfaceDto[]> {
  return useOrganizationClientsListQuery({
    query: { select: selectListRows<ClientAccountSurfaceDto> },
  });
}

export function useDataClientGet(id: string): GetHookResult<ClientAccountSurfaceDto> {
  return useOrganizationClientsReadQuery(id, {
    query: { select: selectDetail<ClientAccountSurfaceDto> },
  });
}

export const DATA_CLIENTS_HOOKS: ResourceHookBinding<
  ClientAccountSurfaceDto[],
  ClientAccountSurfaceDto
> = {
  useList: useDataClientList,
  selectList: selectListRows<ClientAccountSurfaceDto>,
  useGet: useDataClientGet,
  selectDetail: selectDetail<ClientAccountSurfaceDto>,
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'id',
  paramName: 'id',
  listRouteId: ROUTE_IDS.DATA_CLIENTS,
  detailRouteId: ROUTE_IDS.DATA_CLIENT_DETAIL,
  readOperationId: OPERATION_IDS.DATA_CLIENTS_GET,
};

// ── Portfolios ─────────────────────────────────────────────────────────────

export function useDataPortfolioList(): ListHookResult<PortfolioAccountSurfaceDto[]> {
  return useOrganizationPortfoliosListQuery({
    query: { select: selectListRows<PortfolioAccountSurfaceDto> },
  });
}

export function useDataPortfolioGet(id: string): GetHookResult<PortfolioAccountSurfaceDto> {
  return useOrganizationPortfoliosReadQuery(id, {
    query: { select: selectDetail<PortfolioAccountSurfaceDto> },
  });
}

export const DATA_PORTFOLIOS_HOOKS: ResourceHookBinding<
  PortfolioAccountSurfaceDto[],
  PortfolioAccountSurfaceDto
> = {
  useList: useDataPortfolioList,
  selectList: selectListRows<PortfolioAccountSurfaceDto>,
  useGet: useDataPortfolioGet,
  selectDetail: selectDetail<PortfolioAccountSurfaceDto>,
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'id',
  paramName: 'id',
  listRouteId: ROUTE_IDS.DATA_PORTFOLIOS,
  detailRouteId: ROUTE_IDS.DATA_PORTFOLIO_DETAIL,
  readOperationId: OPERATION_IDS.DATA_PORTFOLIOS_GET,
};

// ── Positions ──────────────────────────────────────────────────────────────

function selectPositionListRows(response: unknown): PositionFactSurfaceDto[] {
  return selectApiData(response as ApiSuccessEnvelope<PositionFactSurfaceDto[]>) ?? [];
}

export function useDataPositionList(): ListHookResult<PositionFactSurfaceDto[]> {
  return useInvestmentFactPositionQuery(undefined, { query: { select: selectPositionListRows } });
}

export const DATA_POSITIONS_HOOKS: ResourceHookBinding<PositionFactSurfaceDto[]> = {
  useList: useDataPositionList,
  selectList: selectPositionListRows,
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'positionId',
  paramName: 'positionId',
  listRouteId: ROUTE_IDS.DATA_POSITIONS,
  detailRouteId: ROUTE_IDS.DATA_POSITIONS_DETAIL,
  readOperationId: OPERATION_IDS.DATA_POSITIONS_GET,
};

// ── Journals ───────────────────────────────────────────────────────────────

export function useDataJournalList(): ListHookResult<unknown[]> {
  return useAccountingJournalListQuery({
    query: { select: (r: unknown) => selectListRows<unknown>(r) },
  });
}

export function useDataJournalGet(id: string): GetHookResult<unknown> {
  return useAccountingJournalGetQuery(id, {
    query: { select: (r: unknown) => selectDetail<unknown>(r) },
  });
}

export const DATA_JOURNALS_HOOKS: ResourceHookBinding<unknown[], unknown> = {
  useList: useDataJournalList,
  selectList: r => selectListRows<unknown>(r),
  useGet: useDataJournalGet,
  selectDetail: r => selectDetail<unknown>(r),
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'id',
  paramName: 'id',
  listRouteId: ROUTE_IDS.DATA_JOURNALS,
  detailRouteId: ROUTE_IDS.DATA_JOURNAL_DETAIL,
  readOperationId: OPERATION_IDS.JOURNALS_GET,
};

// ── Fiscal Periods ─────────────────────────────────────────────────────────

export function useDataFiscalPeriodList(): ListHookResult<unknown[]> {
  return useAccountingFiscalPeriodListQuery({
    query: { select: (r: unknown) => selectListRows<unknown>(r) },
  });
}

export const DATA_FISCAL_PERIODS_HOOKS: ResourceHookBinding<unknown[]> = {
  useList: useDataFiscalPeriodList,
  selectList: r => selectListRows<unknown>(r),
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'id',
  paramName: 'id',
  listRouteId: ROUTE_IDS.DATA_FISCAL_PERIODS,
  detailRouteId: ROUTE_IDS.DATA_FISCAL_PERIODS,
  readOperationId: OPERATION_IDS.FISCAL_PERIODS_LIST,
};

// Performance intentionally has no dedicated hook. The generic resource page
// executes query.data.portfolioPerformance.list from the authorized descriptor.

// ── Tax Records (CGT facts) ──────────────────────────────────────────────────

export function useDataTaxRecordList(): ListHookResult<unknown[]> {
  return useInvestmentFactCgtQuery(undefined, {
    query: { select: (r: unknown) => selectListRows<unknown>(r) },
  });
}

export const DATA_TAX_RECORDS_HOOKS: ResourceHookBinding<unknown[]> = {
  useList: useDataTaxRecordList,
  selectList: r => selectListRows<unknown>(r),
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'id',
  paramName: 'id',
  listRouteId: ROUTE_IDS.DATA_TAX_RECORDS,
  detailRouteId: ROUTE_IDS.DATA_TAX_RECORDS,
  readOperationId: OPERATION_IDS.TAX_RECORDS_LIST,
};

// ── Risk Exposures (from position concentration) ──────────────────────────────

export function useDataRiskExposureList(): ListHookResult<unknown[]> {
  return useInvestmentFactPositionQuery(undefined, {
    query: { select: (r: unknown) => selectListRows<unknown>(r) },
  });
}

export const DATA_RISK_EXPOSURES_HOOKS: ResourceHookBinding<unknown[]> = {
  useList: useDataRiskExposureList,
  selectList: r => selectListRows<unknown>(r),
  rowIdExtractor: row => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'instrumentSymbol',
  paramName: 'instrumentSymbol',
  listRouteId: ROUTE_IDS.DATA_RISK_EXPOSURES,
  detailRouteId: ROUTE_IDS.DATA_RISK_EXPOSURES,
  readOperationId: OPERATION_IDS.RISK_EXPOSURES_LIST,
};

// Compliance breaches intentionally use the backend-resolved generic resource
// binding. That prevents the browser from substituting the realized-position
// feed for the persisted compliance lifecycle.
